import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { FaHandshake, FaUserTie, FaUser, FaTractor, FaMapMarkedAlt, FaTint, FaSeedling, FaChartLine } from 'react-icons/fa'; // Added FaTractor & FaMapMarkedAlt
import { MdOutlineNoteAlt } from 'react-icons/md';
import { BsClipboard2DataFill } from 'react-icons/bs';
import { FiAlertCircle, FiAlertTriangle, FiInbox, FiLifeBuoy } from 'react-icons/fi';

import Wrapper from '../../components/Wrapper';
import KPICard, { KPICardSkeleton } from '../../components/Dashboard/KPICard';
import { fetchDashboardFilters, fetchDashboardMetrics } from '../../services/dashboardApi';
import { getRole } from '../../utils/role';
import '../../styles/dashboard.css';
import HealthDistributionChart from '../../components/Dashboard/HealthDistributionChart';

const METRIC_CONFIG = {
  partners: {
    icon: <FaHandshake size={22} />,
    color: 'blue',
    path: '/partners',
  },
  fieldOfficers: {
    icon: <FaUserTie size={22} />,
    color: 'purple',
    path: '/field-officers',
  },
  farmers: {
    icon: <FaUser size={22} />,
    color: 'green',
    path: '/users',
  },
  bookings: {
    icon: <MdOutlineNoteAlt size={22} />,
    color: 'amber',
    path: '/bookings',
  },
  soilReports: { 
    icon: <BsClipboard2DataFill size={20} />, 
    color: 'indigo', 
    path: '/reports' 
  },
  
  waterReports: { 
    icon: <FaTint size={20} />, 
    color: 'sky', 
    path: '/reports' 
  },
  
  soilAnomaliesToday: {
    icon: <FiAlertTriangle size={22} />,
    color: 'red',
    path: '/anomaly-logs',
  },
  soilAnomaliesWeek: {
    icon: <FiAlertTriangle size={22} />,
    color: 'red',
    path: '/anomaly-logs',
  },
  soilAnomaliesTotal: {
    icon: <FiAlertTriangle size={22} />,
    color: 'red',
    path: '/anomaly-logs',
  },
  unresolvedIssues: {
    icon: <FiLifeBuoy size={22} />,
    color: 'orange',
    path: '/support/issues',
  },
  farms: {
    icon: <FaTractor size={22} />,
    color: 'teal',
    path: '/farms',
  },
  totalArea: {
    icon: <FaMapMarkedAlt size={22} />,
    color: 'cyan',
    path: '/farms',
  },
  currentYield: { 
    icon: <FaSeedling size={22} />, 
    color: 'emerald', 
    path: '/reports' 
  },
  yieldGrowth: { 
    icon: <FaChartLine size={20} />, 
    color: 'blue', 
    path: '/reports' 
  },
};

const Home = () => {
  const [metrics, setMetrics] = useState([]);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  
  const [filters, setFilters] = useState({ state: '', city: '', crop: '' });
  const [filterOptions, setFilterOptions] = useState({ states: [], cities: [], crops: [] });

  const role = getRole();

  // Get user data from Redux store based on role
  const { partner } = useSelector((state) => state.partner || {});
  const { fieldOfficerInfo } = useSelector((state) => state.fieldOfficer || {});
  const { farmerInfo } = useSelector((state) => state.farmer || {});

  // Get welcome name based on role
  const getWelcomeName = () => {
    switch (role) {
      case 'PARTNER':
        return partner?.primary_person_name || 'Partner';
      case 'FIELD_OFFICER':
        return fieldOfficerInfo?.name
          ? fieldOfficerInfo.name.charAt(0).toUpperCase() + fieldOfficerInfo.name.slice(1)
          : 'Field Officer';
      case 'FARMER':
        return farmerInfo?.first_name || 'Farmer';
      case 'SUPPORT':
      default:
        return 'Ekosight';
    }
  };

  const loadFilterOptions = useCallback(async () => {
    if ((role === 'PARTNER' || role === 'TEAM_MEMBER')) {
      const data = await fetchDashboardFilters();

      if (data.success) {
        setFilterOptions({
          states: data.states,
          cities: data.cities,
          crops: data.crops,
        });
      }
    }
  }, [role]);

  const loadMetrics = useCallback(async () => {
    setStatus('loading');
    setErrorMsg('');
    try {
      const data = await fetchDashboardMetrics(filters);

      if (data.success) {
        setMetrics(data.metrics);
        setStatus('success');
      } else {
        setErrorMsg(data.message || 'Failed to load metrics');
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || 'Network error');
      setStatus('error');
    }
  }, [filters]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const handleFilterChange = (e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const cardMetrics = metrics.filter(metric => metric.type !== 'chart');

  const chartMetrics = metrics.filter(
    metric =>
      metric.type === 'chart' &&
      (metric.key === 'soilHealthDistribution' ||
        metric.key === 'waterHealthDistribution')
  );

  return (
    <Wrapper>
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p>Welcome back {getWelcomeName()}!</p>
        </div>

        {/* Dashboard Filters */}
        {(role === 'PARTNER' || role === 'TEAM_MEMBER')&& (
          <div className="dashboard-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
            <select name="state" value={filters.state} onChange={handleFilterChange} className="filter-input" style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', flex: "1 1 200px", minWidth: "0", }}>
              <option value="">All States</option>
              {filterOptions.states.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>

            <select name="city" value={filters.city} onChange={handleFilterChange} className="filter-input" style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', flex: "1 1 200px", minWidth: "0", }}>
              <option value="">All Cities</option>
              {filterOptions.cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            <select name="crop" value={filters.crop} onChange={handleFilterChange} className="filter-input" style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc', flex: "1 1 200px", minWidth: "0", }}>
              <option value="">All Crops</option>
              {filterOptions.crops.map((crop) => (
                <option key={crop} value={crop}>{crop}</option>
              ))}
            </select>
          </div>
        )}

        {/* Content */}
        {status === 'loading' && (
          <div className="kpi-grid">
            {Array.from({ length: (role === 'PARTNER' || role === 'TEAM_MEMBER') ? 6 : 4 }).map((_, i) => (
              <KPICardSkeleton key={i} />
            ))}
          </div>
        )}

        {status === 'error' && (
          <div className="dashboard-error">
             <FiAlertCircle />
            <h3>Unable to load dashboard</h3>
            <p>{errorMsg}</p>
            <button onClick={loadMetrics}>Retry</button>
          </div>
        )}

        {status === 'success' && metrics.length === 0 && (
          <div className="dashboard-empty">
            <FiInbox />
            <h3>No metrics available</h3>
            <p>There is no data to display for your role at this time.</p>
          </div>
        )}

        {status === 'success' && metrics.length > 0 && (
          <>
            {/* KPI Cards */}
            <div className="kpi-grid">
              {cardMetrics.map((metric) => {
                const config = METRIC_CONFIG[metric.key] || {};

                return (
                  <KPICard
                    key={metric.key}
                    title={metric.title}
                    count={metric.count}
                    timeFilter={metric.timeFilter}
                    trend={metric.trend}
                    icon={config.icon}
                    color={config.color}
                    path={config.path}
                  />
                );
              })}
            </div>

            {/* Charts */}
            {chartMetrics.length > 0 && (
              <div className="chart-grid">
                {chartMetrics.map((metric) => (
                  <HealthDistributionChart
                    key={metric.key}
                    title={metric.title}
                    data={metric.data}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Wrapper>
  );
};

export default Home;